import { Component, Output, EventEmitter, ElementRef, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Preferences } from '@capacitor/preferences';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { UtilityService } from 'src/app/services/utility.service';
import { DashboardDataService } from 'src/app/services/dashboard-data.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  navItems: { label: string; path: string }[] = [
    { label: 'Dynamic', path: '/utility-dashboard' },
    { label: 'Preferential', path: '/preferential' },
    { label: 'Bid History', path: '/bid-history' },
    { label: 'Settlement', path: '/settlement' },
    { label: 'Meter Readings', path: '/smart-meter-data' },
  ];

  partnerNavItems: { label: string; path: string }[] = [
    { label: 'Dynamic', path: '/partner-dynamic' },
    { label: 'Preferential', path: '/partner-preferential' },
    { label: 'Bid History', path: '/partner-bid-history' },
    { label: 'Settlement', path: '/partner-settlement' },
    { label: 'Meter Readings', path: '/partner-smart-meter-data' },
  ];

  brand: string = '';
  brandLogo: string | null = null;
  logoLoading = false;
  isPartner = false;

  @Output() notifications = new EventEmitter<void>();
  @Output() account = new EventEmitter<void>();

  showSettingsDropdown = false;

  private subscriptions = new Subscription();
  private currentUtilityId: string | null = null;

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private utilityService: UtilityService,
    private dashboardData: DashboardDataService,
    private eRef: ElementRef
  ) { }

  ngOnInit() {
    this.isPartner = this.dashboardData.isPartnerSession();
    if (this.isPartner) {
      this.navItems = this.partnerNavItems;
    }

    const initialUtilityId = this.localStorageService.getUserId();
    const initialBrand = this.localStorageService.getBrand();

    // 1. Immediate initialization from LocalStorage to prevent flicker/compression
    if (initialBrand && initialBrand !== 'undefined') {
      this.brand = initialBrand.toUpperCase();
    } else {
      this.brand = 'UTILITY';
    }

    if (this.isPartner) {
      // Partner identity comes from the partner profile, never from a
      // utility lookup; the customer utility remains BESCOM.
      this.fetchPartnerDetails();
      return;
    }

    if (initialUtilityId) {
      this.fetchUtilityDetails(initialUtilityId);
      this.applyLogo(initialUtilityId);
    }
  }

  private fetchPartnerDetails() {
    const sub = this.dashboardData.getPartnerProfile().subscribe({
      next: (res) => {
        if (res && res.name) {
          this.brand = res.name.toUpperCase();
        }
      },
      error: (err) => {
        console.error('Failed to fetch partner details:', err);
      }
    });
    this.subscriptions.add(sub);
  }

  private fetchUtilityDetails(utilityId: string) {
    const sub = this.utilityService.getUtility(utilityId).subscribe({
      next: (res) => {
        if (res && res.name) {
          this.brand = res.name.toUpperCase();
        }
      },
      error: (err) => {
        console.error('Failed to fetch utility details:', err);
        // Fallback to local storage or generic
        const storedBrand = this.localStorageService.getBrand();
        this.brand = (storedBrand && storedBrand !== 'undefined') ? storedBrand.toUpperCase() : 'UTILITY';
      }
    });
    this.subscriptions.add(sub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();

  }

  applyLogo(utilityId: string) {

    if (utilityId === this.currentUtilityId && this.brandLogo) {
      return;
    }

    this.currentUtilityId = utilityId;
    this.logoLoading = true;
    this.brandLogo = null;

    const logoSub = this.utilityService.loadLogoDataUrl(utilityId).subscribe(url => {
      this.brandLogo = url === 'assets/utility_logo.png' ? null : url;
      this.logoLoading = false;
    });

    this.subscriptions.add(logoSub);
  }

  async logout() {
    this.localStorageService.clear();
    await Preferences.clear();
    this.navigateTo('/welcome');
  }

  navigateTo(path: string) {
    this.router.navigate([path]);
  }

  toggleSettings() {
    this.showSettingsDropdown = !this.showSettingsDropdown;
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.showSettingsDropdown = false;
    }
  }

  onNotifications() {
    this.notifications.emit();
  }

  onAccount() {
    this.account.emit();
  }
}
